import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, IconButton, Link, TableCell, TableRow, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";

import { CostTreeNode } from "@rossum/upcloud-costs-shared";

import { changePercent, formatMoney } from "../../common/format";
import { nodeLabel } from "./node-label";

interface CostTreeRowProps {
    node: CostTreeNode;
    depth: number;
    currency: string | null;
    initiallyExpanded: boolean;
}

/** A row of the cost tree with its collapsible children. */
export function CostTreeRow({ node, depth, currency, initiallyExpanded }: CostTreeRowProps) {
    const { t, i18n } = useTranslation();
    const [expanded, setExpanded] = useState(initiallyExpanded);
    const label = nodeLabel(node, t);
    const change = changePercent(node.amount, node.previousAmount);
    const hasChildren = node.children.length > 0;

    return (
        <>
            <TableRow hover data-testid={`cost-node-${node.kind}`}>
                <TableCell sx={{ pl: 1 + depth * 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {hasChildren ? (
                            <IconButton
                                size="small"
                                aria-expanded={expanded}
                                aria-label={`${expanded ? t("costTree.collapse") : t("costTree.expand")} ${label}`}
                                onClick={() => {
                                    setExpanded(!expanded);
                                }}>
                                {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 34 }} />
                        )}
                        {node.kind === "RESOURCE" && node.cloudResourceId !== undefined ? (
                            <Link component={RouterLink} to={`/resources/${node.cloudResourceId}`}>
                                {label}
                            </Link>
                        ) : (
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: node.kind === "RESOURCE" || node.kind === "CATEGORY" ? 400 : 600 }}>
                                {label}
                            </Typography>
                        )}
                    </Box>
                </TableCell>
                <TableCell align="right">{formatMoney(node.amount, currency, i18n.language)}</TableCell>
                <TableCell align="right">{formatMoney(node.previousAmount, currency, i18n.language)}</TableCell>
                <TableCell align="right">
                    {change === null
                        ? "–"
                        : new Intl.NumberFormat(i18n.language, {
                              style: "percent",
                              maximumFractionDigits: 1,
                              signDisplay: "exceptZero",
                          }).format(change / 100)}
                </TableCell>
            </TableRow>
            {expanded &&
                node.children.map((childNode) => (
                    <CostTreeRow
                        key={`${childNode.kind}:${childNode.code ?? ""}:${childNode.cloudResourceId ?? ""}`}
                        node={childNode}
                        depth={depth + 1}
                        currency={currency}
                        initiallyExpanded={false}
                    />
                ))}
        </>
    );
}
